import { SNACKBAR_LENGTH_SHORT, SnackbarService } from '@app/shared/utils/snackbar.service'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators'

import { Injectable } from '@angular/core'
import { AuthService } from '@app/core/service/auth.service'
import { AuthActions } from '@app/core/store'
import { Store } from '@ngrx/store'
import { keyBy } from 'lodash'
import { of } from 'rxjs'
import { RateCardMetaStructure } from '../models'
import { ApplicationSettingsApiService } from '../services'
import { ApplicationSettingsMappingService } from '../services/application-settings-mapping.service'
import { ApplicationSettingsActions } from './application-settings.actions'
import { ApplicationSettingsSelectors } from './application-settings.selectors'

@Injectable()
export class ApplicationSettingsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store,
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private apiService: ApplicationSettingsApiService,
    private mappingService: ApplicationSettingsMappingService
  ) {}

  getRateCardMetaStructure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationSettingsActions.getRateCardMetaStructure),
      switchMap(() =>
        this.apiService.getRateCardMetaStructure().pipe(
          map((rateCardMetaStructure) =>
            ApplicationSettingsActions.getRateCardMetaStructureSuccess({ rateCardMetaStructure })
          ),
          catchError((error) => of(ApplicationSettingsActions.getRateCardMetaStructureFail({ error })))
        )
      )
    )
  )

  saveRateCardMetaStructure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationSettingsActions.saveRateCardMetaStructure),
      withLatestFrom(this.store$.select(ApplicationSettingsSelectors.selectRateCardMetaStructure)),
      switchMap(([{ fields }, metaStructure]) => {
        const updatedMetaStructure: RateCardMetaStructure = {
          ...metaStructure,
          ratecardMetaStructureDefinition: {
            ...metaStructure?.ratecardMetaStructureDefinition,
            roles: { additionalFields: fields },
          },
        }

        return this.apiService.saveRateCardMetaStructure(updatedMetaStructure).pipe(
          map((rateCardMetaStructure) =>
            ApplicationSettingsActions.saveRateCardMetaStructureSuccess({ rateCardMetaStructure })
          ),
          catchError((error) => of(ApplicationSettingsActions.saveRateCardMetaStructureFail({ error })))
        )
      })
    )
  )

  getApplicationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationSettingsActions.getApplicationSettings),
      switchMap(() =>
        this.apiService.getApplicationSettings().pipe(
          map((response) => {
            const companySettingsMap = keyBy(
              this.authService.loggedInUser.company.applicationSettings,
              'applicationSettingType'
            )
            const settings = response.map((setting) => ({ ...setting, ...companySettingsMap[setting.setting] }))
            return ApplicationSettingsActions.getApplicationSettingsSuccess({ settings })
          }),
          catchError((error) => of(ApplicationSettingsActions.getApplicationSettingsFail({ error })))
        )
      )
    )
  )

  updateApplicationSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationSettingsActions.updateApplicationSetting),
      mergeMap(({ setting }) =>
        this.apiService.updateApplicationSetting(this.mappingService.mapApplicationSettingToApi(setting)).pipe(
          map((response) => ApplicationSettingsActions.updateApplicationSettingSuccess({ setting, response })),
          catchError((error) => of(ApplicationSettingsActions.updateApplicationSettingFail({ error, setting })))
        )
      )
    )
  )

  setApplicationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationSettingsActions.updateApplicationSettingSuccess),
      withLatestFrom(this.store$.select(ApplicationSettingsSelectors.selectMergedApplicationSettings)),
      switchMap(([{ response }, allSettings]) => {
        const settings = allSettings.map((s) => {
          const updatedSetting = response.find((r) => r.applicationSettingType === s.setting)
          return updatedSetting ? { ...s, ...updatedSetting } : s
        })
        const applicationSettings = this.mappingService.mapApplicationSettingsToApi(settings)

        return [
          ApplicationSettingsActions.setApplicationSettings({ settings }),
          AuthActions.updateApplicationSettings({ applicationSettings }),
        ]
      })
    )
  )

  clearTrafficImportCache$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationSettingsActions.clearTrafficImportCache),
      switchMap(() =>
        this.apiService.clearTrafficImportCache().pipe(
          map(() => {
            this.snackbarService.showSuccessSnackbar('Traffic Import Cache cleared', SNACKBAR_LENGTH_SHORT)
            return ApplicationSettingsActions.clearTrafficImportCacheSuccess()
          }),
          catchError((error) => of(ApplicationSettingsActions.clearTrafficImportCacheFail({ error })))
        )
      )
    )
  )
}
